import React, { type JSX } from "react";
import ReactDOM from "react-dom/client";

import type { ThelabuiContextProcessorsTranslationProcessor } from "@reactivated";

import { getJSONScriptData } from "./utils/dom";
import { activateTranslations } from "./utils/i18n";

type BaseContext = {
    template_name: string;
} & {
    i18n?: ThelabuiContextProcessorsTranslationProcessor["i18n"];
};

type InitPageArgs<T extends BaseContext> = {
    getServerData: () => {
        props: Record<string, unknown>;
        context: T;
    };

    getTemplate: ({
        template_name,
    }: {
        template_name: string;
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    }) => Promise<React.ComponentType<any>>;

    Provider: (props: { value: T; children: React.ReactNode }) => JSX.Element;
};

/**
 * Find and rehydrate all of the React components on the page.
 */

export const initPage = async <T extends BaseContext>({
    Provider,
    getServerData,
    getTemplate,
}: InitPageArgs<T>): Promise<void> => {
    type ServerData = ReturnType<typeof getServerData>;
    type ServerContext = ServerData["context"];
    type ServerProps = ServerData["props"];

    const renderTemplate = async (
        rootElem: HTMLElement | Document,
        context: ServerContext,
        props: ServerProps,
    ): Promise<void> => {
        const Template = await getTemplate(context);
        ReactDOM.hydrateRoot(
            rootElem,
            <React.StrictMode>
                <Provider value={context}>
                    <Template {...props} />
                </Provider>
            </React.StrictMode>,
        );
    };

    /**
     * Rehydrate a standard full-react page.
     */
    const renderFullReactPage = async (): Promise<void> => {
        const { props, context } = getServerData();
        if (context.i18n) {
            activateTranslations(context.i18n);
        }
        await renderTemplate(document, context, props);
    };

    /**
     * Find and rehydrate all of the React components on the page that
     * were included by means of the include_react template tag.
     */
    const renderReactIncludes = async (): Promise<void> => {
        const context = getJSONScriptData("thelabui-react-context") as T | null;
        if (!context) {
            console.warn("thelabui-react-context is missing or invalid");
            return;
        }
        if (context.i18n) {
            activateTranslations(context.i18n);
        }
        const elems = document.querySelectorAll<HTMLElement>(
            "[data-include-react]",
        );
        const rendering = [...elems].map((elem) => {
            const templateName = elem.dataset.template!;
            const propsScriptID = elem.dataset.props;
            const props = propsScriptID
                ? getJSONScriptData(propsScriptID)
                : null;
            return renderTemplate(
                elem,
                { ...context, template_name: templateName },
                props as Record<string, unknown>,
            );
        });
        await Promise.all(rendering);
    };

    // Figure out what type of page this is (either full-React, or mixed Django
    // and React), and rehydrate all of the components on the page.
    /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,@typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-member-access */
    if (window && (window as any).__PRELOADED_PROPS__) {
        await renderFullReactPage();
    } else {
        await renderReactIncludes();
    }
};
